import { Alert, FormControl, IconButton, TextField } from "@mui/material";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useUpdateOrderMutation } from "src/api/services/ordersApi";
import { useGetPrescriptionDetailsQuery } from "src/api/services/prescriptionsApi";
import { OrderWithPrescription } from "src/api/types";
import UpdateIcon from "@mui/icons-material/Update";
import CancelIcon from "@mui/icons-material/Cancel";
import { format } from "date-fns";
import OrderStatusChip from "src/components/OrderStatusChip";
import BillingModel from "src/components/BillingModel";
import { usePatientOrdersModal } from "src/context/PatientOrdersModalProvider/usePatientOrdersModal";
import { Edit } from "@mui/icons-material";
import MedicationInstruction from "../MedicationInstruction/MedicationInstruction";
import InvoiceInformation from "../InvoiceInformation";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";

interface Props {
  order: OrderWithPrescription;
}

const PatientOrder = ({ order }: Props) => {
  const { openInstructionsModal } = usePatientOrdersModal();
  const clinicianRoles = useClinician((c) => c.roles);
  const { data: prescriptionDetails } = useGetPrescriptionDetailsQuery(
    order.prescription_id,
  );
  const [updateOrder] = useUpdateOrderMutation();
  const [trackingNumber, setTrackingNumber] = useState<string>("");
  const [edit, setEdit] = useState<boolean>(false);

  const canViewInvoice = useMemo(() => {
    return clinicianRoles.some((r) =>
      ["Coordinator", "Admin", "Operations", "Developer"].includes(r.name),
    );
  }, [clinicianRoles]);

  const handleTrackingNumber = useCallback(
    async (orderId: number) => {
      if (!orderId) return null;
      try {
        await updateOrder({
          id: orderId,
          tracking_number: trackingNumber,
          shipped_at: new Date().toISOString(),
        }).unwrap();
        setEdit(false);
      } catch (err) {
        console.error("Error updating tracking number", err);
      }
    },
    [trackingNumber, updateOrder],
  );

  const handleOpen = useCallback(() => {
    openInstructionsModal(prescriptionDetails);
  }, [openInstructionsModal, prescriptionDetails]);

  const medication = useMemo(() => {
    return prescriptionDetails?.medications[0] ?? null;
  }, [prescriptionDetails?.medications]);

  const medicationName = useMemo(() => {
    if (medication?.form === "oral") {
      return `${medication.name} [${medication.strength}]`;
    }

    const name = medication?.name;
    const dosageUnit = medication?.dosage.replace(/\d+([,.]\d+)?/g, "");
    const totalDose = (prescriptionDetails?.medications ?? []).reduce(
      (acc, m) => (acc += parseFloat(m.dosage)),
      0,
    );

    if (name == null) {
      return order.product.display_name;
    }

    if (!totalDose && dosageUnit === "N/A") {
      return name;
    }

    return `${name} [${totalDose} ${dosageUnit}]`;
  }, [
    medication?.dosage,
    medication?.form,
    medication?.name,
    medication?.strength,
    order.product.display_name,
    prescriptionDetails?.medications,
  ]);

  const quantity = useMemo(() => {
    const form = medication?.form;
    const dispenseQuantity =
      order.product.slug === "bc"
        ? Number(order.prescription.dispense_quantity) / 28
        : order.prescription.dispense_quantity;

    if (["ec", "bc", "bc-ot"].includes(order.product.slug)) {
      return `${dispenseQuantity} pack(s)`;
    }

    if (form === "oral") {
      return `${dispenseQuantity} tablet(s)`;
    }

    return null;
  }, [
    medication?.form,
    order.prescription.dispense_quantity,
    order.product.slug,
  ]);

  const pharmacyName = useMemo(() => {
    if (
      order.sent_to_external_pharmacy &&
      order?.external_pharmacy?.name != null
    ) {
      return order.external_pharmacy.name;
    }

    return order.pharmacy.name;
  }, [
    order?.external_pharmacy?.name,
    order.pharmacy.name,
    order.sent_to_external_pharmacy,
  ]);

  return (
    <Stack
      key={order.id}
      gap={1.5}
      p={1}
      sx={{ border: "1px solid #000000", borderRadius: "5px" }}
    >
      {order.is_delayed ? (
        <Alert severity="warning">This order is Delayed</Alert>
      ) : null}
      <Stack direction="row" gap={1} flexWrap="wrap" alignContent="center">
        <Typography fontWeight={700} variant="h5">{`#${order.id}`}</Typography>
        <Typography
          fontWeight={700}
          variant="h5"
        >{`${pharmacyName}`}</Typography>
        <BillingModel model={order.product.billing_model} />
        <OrderStatusChip status={order.status} />
      </Stack>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Stack gap={1}>
          <Typography
            component={"em"}
            fontWeight={600}
          >{`Medications:`}</Typography>
          <Stack direction={"row"}>
            <Typography ml={1}>{medicationName}</Typography>
            {quantity ? <Typography ml={1}>- {quantity}</Typography> : null}
          </Stack>
        </Stack>
        {canViewInvoice ? <InvoiceInformation order={order} /> : null}
      </Stack>
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            component={"em"}
            fontWeight={600}
          >{`Dosage Instructions:`}</Typography>
          {medication?.form === "injectable" ? (
            <IconButton onClick={handleOpen}>
              <Edit />
            </IconButton>
          ) : null}
        </Stack>
        {prescriptionDetails?.instructions.map((instruction, index) => (
          <MedicationInstruction
            key={`${instruction.id}-${index}`}
            instruction={instruction}
            medication={medication}
            index={index}
            isInjectable={medication?.form === "injectable"}
          />
        ))}
      </Stack>

      <Stack gap={0}>
        {order.prescription.provider ? (
          <Typography
            sx={{ fontStyle: "italic" }}
          >{`Prescriber: ${order.prescription.provider.profile.first_name}  ${order.prescription.provider.profile.last_name}`}</Typography>
        ) : null}
        <Typography sx={{ fontStyle: "italic" }}>{`Created at: ${format(
          new Date(order.created_at),
          "Pp",
        )}`}</Typography>
        {order.delivered_at != null ? (
          <Typography sx={{ fontStyle: "italic" }}>{`Delivered at: ${format(
            new Date(order.delivered_at),
            "Pp",
          )}`}</Typography>
        ) : null}
      </Stack>

      {order.tracking_number && !edit ? (
        <Stack direction="row" alignItems="center">
          <Typography>Tracking #: {order.tracking_number}</Typography>
          <IconButton size="small" onClick={() => setEdit((e) => !e)}>
            <UpdateIcon />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row">
          <FormControl fullWidth>
            <TextField
              placeholder="Tracking number"
              size="small"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
            />
          </FormControl>
          <IconButton
            size="small"
            onClick={() => handleTrackingNumber(order.id)}
          >
            <UpdateIcon />
          </IconButton>
          <IconButton size="small" onClick={() => setEdit((e) => !e)}>
            <CancelIcon />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};

export default PatientOrder;
